import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from './ui/dialog';
import { Button } from './ui/button';
import { Input } from './ui/input';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// Self-contained Friends & Family manager modal.
// Parent only controls open/close + supplies the owning user's email/id.
export const FriendsFamilyPanel = ({ open, onOpenChange, userEmail }) => {
  const [friendsFamilyList, setFriendsFamilyList] = useState([]);
  const [newContact, setNewContact] = useState({ name: '', email: '', phone: '', company: '' });
  const [editingContact, setEditingContact] = useState(null);
  const [contactMessage, setContactMessage] = useState('');

  // Fetch list whenever the panel opens (and we have an identifier)
  useEffect(() => {
    if (!open || !userEmail) return;
    (async () => {
      try {
        const response = await axios.get(`${API}/users/${userEmail}/friends-family`);
        if (response.data.success) {
          setFriendsFamilyList(response.data.contacts || []);
        }
      } catch (error) {
        console.error('Error fetching friends & family:', error);
      }
    })();
  }, [open, userEmail]);

  const handleAddContact = async () => {
    if (!newContact.name) {
      setContactMessage('Name is required');
      return;
    }
    try {
      const response = await axios.post(`${API}/users/${userEmail}/friends-family`, newContact);
      if (response.data.success) {
        setFriendsFamilyList([...friendsFamilyList, response.data.contact]);
        setNewContact({ name: '', email: '', phone: '', company: '' });
        setContactMessage('Contact added successfully!');
        setTimeout(() => setContactMessage(''), 2000);
      }
    } catch (error) {
      console.error('Error adding contact:', error);
      const errorMsg = error.response?.data?.detail || 'Failed to add contact';
      setContactMessage(errorMsg);
      setTimeout(() => setContactMessage(''), 4000);
    }
  };

  const handleUpdateContact = async (contactId) => {
    if (!editingContact?.name) {
      setContactMessage('Name is required');
      return;
    }
    try {
      const response = await axios.put(`${API}/users/${userEmail}/friends-family/${contactId}`, editingContact);
      if (response.data.success) {
        setFriendsFamilyList(friendsFamilyList.map(c => c.id === contactId ? { ...c, ...editingContact } : c));
        setEditingContact(null);
        setContactMessage('Contact updated!');
        setTimeout(() => setContactMessage(''), 2000);
      }
    } catch (error) {
      console.error('Error updating contact:', error);
      setContactMessage('Failed to update contact');
    }
  };

  const handleDeleteContact = async (contactId) => {
    if (!window.confirm('Are you sure you want to delete this contact?')) return;
    try {
      const response = await axios.delete(`${API}/users/${userEmail}/friends-family/${contactId}`);
      if (response.data.success) {
        setFriendsFamilyList(friendsFamilyList.filter(c => c.id !== contactId));
        setContactMessage('Contact deleted');
        setTimeout(() => setContactMessage(''), 2000);
      }
    } catch (error) {
      console.error('Error deleting contact:', error);
      setContactMessage('Failed to delete contact');
    }
  };

  const handleToggleEmailNotifications = async (contact) => {
    const newValue = contact.receives_emails === false ? true : false;
    try {
      const response = await axios.put(`${API}/users/${userEmail}/friends-family/${contact.id}`, {
        name: contact.name,
        email: contact.email || '',
        phone: contact.phone || '',
        receives_emails: newValue,
        is_business: contact.is_business || false,
      });
      if (response.data.success) {
        setFriendsFamilyList(friendsFamilyList.map(c =>
          c.id === contact.id ? { ...c, receives_emails: newValue } : c
        ));
        setContactMessage(newValue ? 'Email notifications enabled' : 'Email notifications disabled');
        setTimeout(() => setContactMessage(''), 2000);
      }
    } catch (error) {
      console.error('Error toggling email notifications:', error);
      setContactMessage('Failed to update settings');
    }
  };

  const handleToggleBusiness = async (contact) => {
    const newValue = !contact.is_business;
    try {
      const response = await axios.put(`${API}/users/${userEmail}/friends-family/${contact.id}`, {
        name: contact.name,
        email: contact.email || '',
        phone: contact.phone || '',
        receives_emails: contact.receives_emails !== false,
        is_business: newValue,
      });
      if (response.data.success) {
        setFriendsFamilyList(friendsFamilyList.map(c =>
          c.id === contact.id ? { ...c, is_business: newValue } : c
        ));
        setContactMessage(newValue ? 'Marked as business contact' : 'Removed business flag');
        setTimeout(() => setContactMessage(''), 2000);
      }
    } catch (error) {
      console.error('Error toggling business flag:', error);
      setContactMessage('Failed to update');
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        className="sm:max-w-lg max-h-[80vh] overflow-y-auto border-2 border-cyan-400/70"
        style={{
          boxShadow: '0 0 15px rgba(34, 211, 238, 0.4), 0 0 30px rgba(34, 211, 238, 0.2), 0 0 45px rgba(34, 211, 238, 0.1)',
        }}
      >
        <DialogHeader>
          <DialogTitle className="text-xl flex items-center gap-2">
            👥 Friends & Family
          </DialogTitle>
          <DialogDescription>
            Manage contacts you can assign tickets to
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4 py-4">
          {contactMessage && (
            <div className={`p-2 rounded text-sm text-center ${
              contactMessage.includes('success') || contactMessage.includes('updated') || contactMessage.includes('deleted')
                ? 'bg-green-100 text-green-800'
                : 'bg-red-100 text-red-800'
            }`}>
              {contactMessage}
            </div>
          )}

          {/* Add New Contact Form */}
          <div className="border rounded-lg p-4 bg-gray-50">
            <h4 className="font-medium mb-3 text-sm">Add New Contact</h4>
            <div className="grid grid-cols-1 gap-3">
              <Input
                placeholder="Name *"
                value={newContact.name}
                onChange={(e) => setNewContact({ ...newContact, name: e.target.value })}
                className="bg-white text-gray-900 border-gray-300"
                style={{ color: '#111827' }}
              />
              <Input
                type="email"
                placeholder="Email"
                value={newContact.email}
                onChange={(e) => setNewContact({ ...newContact, email: e.target.value })}
                className="bg-white text-gray-900 border-gray-300"
                style={{ color: '#111827' }}
              />
              <Input
                type="tel"
                placeholder="Cell Phone (for texts)"
                value={newContact.phone}
                onChange={(e) => setNewContact({ ...newContact, phone: e.target.value })}
                className="bg-white text-gray-900 border-gray-300"
                style={{ color: '#111827' }}
              />
              <Input
                type="text"
                placeholder="Company (for business contacts)"
                value={newContact.company}
                onChange={(e) => setNewContact({ ...newContact, company: e.target.value })}
                className="bg-white text-gray-900 border-gray-300"
                style={{ color: '#111827' }}
                data-testid="new-contact-company-input"
              />
              <Button onClick={handleAddContact} className="w-full">
                ➕ Add Contact
              </Button>
            </div>
          </div>

          {/* Contacts List */}
          <div className="space-y-2">
            <h4 className="font-medium text-sm">
              Your Contacts ({friendsFamilyList.length})
            </h4>

            {friendsFamilyList.length === 0 ? (
              <div className="text-center py-6 text-gray-500 text-sm">
                No contacts yet. Add friends and family above!
              </div>
            ) : (
              <div className="space-y-2 max-h-[300px] overflow-y-auto">
                {friendsFamilyList.map((contact) => (
                  <div key={contact.id} className="border rounded-lg p-3 bg-white">
                    {editingContact?.id === contact.id ? (
                      <div className="space-y-2">
                        <Input
                          placeholder="Name *"
                          value={editingContact.name}
                          onChange={(e) => setEditingContact({ ...editingContact, name: e.target.value })}
                          className="text-sm bg-white text-gray-900 border-gray-300"
                          style={{ color: '#111827' }}
                        />
                        <Input
                          type="email"
                          placeholder="Email"
                          value={editingContact.email || ''}
                          onChange={(e) => setEditingContact({ ...editingContact, email: e.target.value })}
                          className="text-sm bg-white text-gray-900 border-gray-300"
                          style={{ color: '#111827' }}
                        />
                        <Input
                          type="tel"
                          placeholder="Cell Phone"
                          value={editingContact.phone || ''}
                          onChange={(e) => setEditingContact({ ...editingContact, phone: e.target.value })}
                          className="text-sm bg-white text-gray-900 border-gray-300"
                          style={{ color: '#111827' }}
                        />
                        <Input
                          type="text"
                          placeholder="Company"
                          value={editingContact.company || ''}
                          onChange={(e) => setEditingContact({ ...editingContact, company: e.target.value })}
                          className="text-sm bg-white text-gray-900 border-gray-300"
                          style={{ color: '#111827' }}
                        />
                        <div className="flex gap-2">
                          <Button size="sm" onClick={() => handleUpdateContact(contact.id)} className="flex-1">
                            Save
                          </Button>
                          <Button size="sm" variant="outline" onClick={() => setEditingContact(null)} className="flex-1">
                            Cancel
                          </Button>
                        </div>
                      </div>
                    ) : (
                      <div className="flex items-start justify-between">
                        <div className="space-y-1 flex-1">
                          <div className="font-medium text-gray-900">{contact.name}</div>
                          {contact.email && (
                            <div className="text-sm text-gray-600 flex items-center gap-1">
                              📧 {contact.email}
                            </div>
                          )}
                          {contact.phone && (
                            <div className="text-sm text-gray-600 flex items-center gap-1">
                              📱 {contact.phone}
                            </div>
                          )}
                          {contact.company && (
                            <div className="text-sm text-gray-600 flex items-center gap-1">
                              🏢 {contact.company}
                            </div>
                          )}
                          {contact.email && (
                            <div className="flex items-center gap-2 mt-2 pt-2 border-t">
                              <button
                                onClick={() => handleToggleEmailNotifications(contact)}
                                className={`relative inline-flex h-5 w-9 items-center rounded-full transition-colors ${
                                  contact.receives_emails !== false ? 'bg-green-500' : 'bg-gray-300'
                                }`}
                                title={contact.receives_emails !== false ? 'Click to disable emails' : 'Click to enable emails'}
                              >
                                <span
                                  className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${
                                    contact.receives_emails !== false ? 'translate-x-4' : 'translate-x-0.5'
                                  }`}
                                />
                              </button>
                              <span className="text-xs text-gray-600">
                                {contact.receives_emails !== false ? 'Receives reminder emails' : 'No reminder emails'}
                              </span>
                            </div>
                          )}
                          <div className="flex items-center gap-2 mt-2 pt-2 border-t">
                            <button
                              onClick={() => handleToggleBusiness(contact)}
                              className={`relative inline-flex h-5 w-9 items-center rounded-full transition-colors ${
                                contact.is_business ? 'bg-blue-600' : 'bg-gray-300'
                              }`}
                              title={contact.is_business ? 'Click to remove business flag' : 'Click to mark as business'}
                              data-testid={`business-toggle-${contact.id}`}
                            >
                              <span
                                className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${
                                  contact.is_business ? 'translate-x-4' : 'translate-x-0.5'
                                }`}
                              />
                            </button>
                            <span className="text-xs text-gray-600">
                              {contact.is_business ? '💼 Business contact' : 'Personal contact'}
                            </span>
                          </div>
                        </div>
                        <div className="flex gap-1">
                          <button
                            onClick={() => setEditingContact({ ...contact })}
                            className="p-1.5 hover:bg-gray-100 rounded text-blue-600"
                            title="Edit"
                          >
                            ✏️
                          </button>
                          <button
                            onClick={() => handleDeleteContact(contact.id)}
                            className="p-1.5 hover:bg-gray-100 rounded text-red-600"
                            title="Delete"
                          >
                            🗑️
                          </button>
                        </div>
                      </div>
                    )}
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default FriendsFamilyPanel;
